import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  AppFormikButtonTiles,
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';
import PageHeader from 'shared/components/PageHeader';
import { ReactComponent as Edit } from 'assets/images/svgrepo/edit-[#1482].svg';
import { useNavigate, useParams } from 'react-router-dom';
import LaunchIcon from '@mui/icons-material/Launch';
import { useEffect, useState } from 'react';
import { Utils } from 'shared/helpers';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { Formik } from 'formik';
import LinkedAISystems from './components/LinkedAISystems';
import LinkedDatasets from './components/LinkedDatasets';
import { IVulenerability } from '../components/VulnerabilitiesCard';
import { VulnerabilityService } from 'services/VulnerabilityService';
import LinkedIncidents from './components/LinkedIncidents';
import LinkedRisks from './components/LinkedRisks';
import CommentsSection from 'modules/ai-inventory/ai-system/ai-systems-detail/components/CommentsSection';
import { EntityType } from 'shared/enums';
import AuditTrail from 'modules/ai-inventory/datasets/records-dataset/components/AuditTrail';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { useSelector } from 'react-redux';
import { IAppState } from 'state/RootReducer';

const ViewVulnerability: React.FC = () => {
  const navigate = useNavigate();
  const { vulnerabilityId } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [vulnerabilityDetail, setVulnerabilityDetail] =
    useState<IVulenerability>();
  const { orgACLConfig } = useSelector(
    (state: IAppState) => state.baseAppState
  );

  // API Calls
  const loadDetail = (id: string): void => {
    setIsLoading(!vulnerabilityDetail);
    VulnerabilityService.detail(id)
      .then((response) => {
        const { data } = response.data;
        setVulnerabilityDetail(data);
        setIsLoading(false);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        navigate(-1);
      });
  };

  // Hooks
  useEffect(() => {
    if (vulnerabilityId) {
      loadDetail(vulnerabilityId);
    }
  }, [vulnerabilityId]);

  // UI Elements
  return (
    <>
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && vulnerabilityDetail && (
        <>
          <PageHeader
            title={vulnerabilityDetail?.title}
            leftBackButton={
              <AppPrimaryButtonOutlined
                startIcon={<ChevronLeftIcon />}
                className="btn-small"
                title="Back"
                onClick={() => navigate(-1)}
              />
            }
            actions={
              <Stack direction="row" spacing={2}>
                <AppPrimaryButtonOutlined
                  title="Vulnerability Management"
                  startIcon={<LaunchIcon />}
                  onClick={() => {
                    navigate('/vulnerabilities');
                  }}
                />
                {orgACLConfig.vulnerabilities?.includes(
                  'vulnerabilities.aiVulnerabilities.edit'
                ) && (
                  <AppPrimaryButton
                    title="Edit Vulnerability"
                    startIcon={
                      <SvgIcon
                        component={Edit}
                        inheritViewBox
                        className="view-icon"
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    onClick={() => {
                      navigate(`/vulnerabilities/edit/${vulnerabilityId}`);
                    }}
                  />
                )}
              </Stack>
            }
          />
          <Box className="mx-8">
            <Stack
              className="mb-4"
              direction="row"
              spacing={2}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              <Stack alignItems="center" direction="row" spacing={1}>
                <AccessTimeIcon />
                <Typography>
                  Created At: {Utils.formatDate(vulnerabilityDetail.createdAt)}
                </Typography>
              </Stack>

              <Chip label={vulnerabilityDetail.status} />
              <Chip label={vulnerabilityDetail.risk_rating} />
              <Chip label={vulnerabilityDetail.owner_assignee} />

              <Stack alignItems="center" direction="row" spacing={1}>
                <PersonOutlineIcon />
                <Typography>
                  Target Date:{' '}
                  {vulnerabilityDetail.target_remediation_date
                    ? Utils.formatDate(
                        vulnerabilityDetail.target_remediation_date
                      )
                    : 'N/A'}
                </Typography>
              </Stack>
            </Stack>

            <Formik
              initialValues={{ status: [true, false, false] }}
              onSubmit={() => console.log('test')}
            >
              {({ values }) => (
                <>
                  <AppFormikButtonTiles
                    variant="outlined"
                    name="status"
                    size="small"
                    label=""
                    disabled={false}
                    multichoice={false}
                    className="app-group-button mb-5"
                    buttons={[
                      {
                        value: 'Details',
                        tooltip:
                          'View core vulnerability information including severity levels, impact status, and patch availability.',
                      },
                      {
                        value: 'Related Assets',
                        tooltip:
                          'Identify systems, datasets, and application endpoints affected by or exposed to this vulnerability.',
                      },
                      {
                        value: 'History',
                        tooltip:
                          'Track the chronological audit trail of detection events, status updates, score revisions, and remediation actions.',
                      },
                      {
                        value: 'Comments',
                        tooltip:
                          'Collaborate with team members, record notes, and track internal discussions regarding this vulnerability.',
                      },
                    ]}
                    outerLabel
                  />
                  {values.status[0] && (
                    <Grid container spacing={4}>
                      <Grid item xs={12} sm={8}>
                        <Box className="incident-box">
                          <Box className="header">
                            <img height={20} src="/images/icons/document.svg" />
                            <Typography className="title">
                              Description
                            </Typography>
                          </Box>
                          <Typography>
                            {vulnerabilityDetail.description}
                          </Typography>
                          {!vulnerabilityDetail.description && (
                            <Typography className="text-gray">
                              No description added
                            </Typography>
                          )}
                        </Box>
                        <Box className="incident-box">
                          <Box className="header">
                            <img height={20} src="/images/icons/warning.svg" />
                            <Typography className="title">
                              Exploitability Details
                            </Typography>
                          </Box>
                          <Typography>
                            {vulnerabilityDetail.exploitability_details}
                          </Typography>
                          {!vulnerabilityDetail.exploitability_details && (
                            <Typography className="text-gray">
                              No exploitability details provided
                            </Typography>
                          )}
                        </Box>
                        <Box className="incident-box">
                          <Box className="header">
                            <img
                              height={20}
                              src="/images/icons/IconLayer.svg"
                            />
                            <Typography className="title">
                              Business & Regulatory Impact
                            </Typography>
                          </Box>
                          <Typography>
                            {vulnerabilityDetail.business_impact}
                          </Typography>
                          {!vulnerabilityDetail.business_impact && (
                            <Typography className="text-gray">
                              No business impact information provided
                            </Typography>
                          )}
                        </Box>
                        <Box className="incident-box">
                          <Box className="header">
                            <img
                              height={20}
                              src="/images/icons/IconInfoCircle.svg"
                            />
                            <Typography className="title">
                              Remediation Information
                            </Typography>
                          </Box>
                          {!vulnerabilityDetail.verification_evidence &&
                            !vulnerabilityDetail.residual_risk &&
                            !vulnerabilityDetail.compliance_notes && (
                              <Typography className="text-gray">
                                No mitigation actions recorded
                              </Typography>
                            )}

                          <Box className="header mt-5">
                            <Typography className="title">
                              Verification Evidence
                            </Typography>
                          </Box>

                          <Typography>
                            {vulnerabilityDetail.verification_evidence}
                          </Typography>
                          {!vulnerabilityDetail.verification_evidence && (
                            <Typography className="text-gray">
                              No verification evidence provided
                            </Typography>
                          )}

                          <Divider className="my-2" />

                          <Box className="header">
                            <Typography className="title">
                              Residual Risk
                            </Typography>
                          </Box>

                          <Typography>
                            {vulnerabilityDetail.residual_risk}
                          </Typography>
                          {!vulnerabilityDetail.residual_risk && (
                            <Typography className="text-gray">
                              No residual risk information provided
                            </Typography>
                          )}

                          <Divider className="my-2" />

                          <Box className="header">
                            <Typography className="title">
                              Compliance Notes
                            </Typography>
                          </Box>

                          <Typography>
                            {vulnerabilityDetail.compliance_notes}
                          </Typography>
                          {!vulnerabilityDetail.compliance_notes && (
                            <Typography className="text-gray">
                              No residual risk information provided
                            </Typography>
                          )}
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Box className="incident-box">
                          <Box className="header">
                            <Typography className="title">
                              Classification
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Category</Typography>
                            <Typography>
                              {vulnerabilityDetail.vulnerability_category}
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Status</Typography>
                            <Chip label={vulnerabilityDetail.status} />
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Risk Rating</Typography>
                            <Chip label={vulnerabilityDetail.risk_rating} />
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Environment</Typography>
                            <Typography>
                              {vulnerabilityDetail.environment}
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Confidentiality</Typography>
                            <Typography>
                              {
                                vulnerabilityDetail.confidentiality_classification
                              }
                            </Typography>
                          </Box>
                        </Box>

                        <Box className="incident-box">
                          <Box className="header mb-5">
                            <Typography className="title">
                              Dates & Assignment
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Created</Typography>
                            <Typography>
                              {Utils.formatDate(vulnerabilityDetail.createdAt)}
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Discovered</Typography>
                            <Typography>
                              {Utils.formatDate(vulnerabilityDetail.createdAt)}
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Last Updated</Typography>
                            <Typography>
                              {Utils.formatDate(vulnerabilityDetail.updatedAt)}
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography>Owner</Typography>
                            <Typography>
                              {vulnerabilityDetail.owner_assignee}
                            </Typography>
                          </Box>
                        </Box>

                        <Box className="incident-box">
                          <Box className="header mb-5">
                            <Typography className="title">
                              Technical Details
                            </Typography>
                          </Box>
                          <Box className="incident-detail">
                            <Typography className="text-gray">
                              No technical details provided
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    </Grid>
                  )}
                  {values.status[1] && (
                    <Grid spacing={5} className="mb-10" container>
                      <Grid item xs={12} sm={6}>
                        <LinkedAISystems
                          linkedSystems={vulnerabilityDetail.aisystemId}
                          vulnerabilityId={`${vulnerabilityId}`}
                          reloadDetail={() => {
                            loadDetail(`${vulnerabilityId}`);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LinkedDatasets
                          linkedDatasets={vulnerabilityDetail.datasetId}
                          vulnerabilityId={`${vulnerabilityId}`}
                          reloadDetail={() => {
                            loadDetail(`${vulnerabilityId}`);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LinkedIncidents
                          linkedIncidents={vulnerabilityDetail.incidentId}
                          vulnerabilityId={`${vulnerabilityId}`}
                          reloadDetail={() => {
                            loadDetail(`${vulnerabilityId}`);
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <LinkedRisks
                          linkedRisks={vulnerabilityDetail.riskId}
                          vulnerabilityId={`${vulnerabilityId}`}
                          reloadDetail={() => {
                            loadDetail(`${vulnerabilityId}`);
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                  {values.status[2] && (
                    <>
                      <AuditTrail
                        title="History"
                        entityType={EntityType.VULNERABILITY}
                      />
                    </>
                  )}
                  {values.status[3] && (
                    <>
                      <CommentsSection entityType={EntityType.VULNERABILITY} />
                    </>
                  )}
                </>
              )}
            </Formik>
          </Box>
        </>
      )}
    </>
  );
};

export default ViewVulnerability;
